import type { HelpdeskStatusGroup, HelpdeskZendeskStatus, IHelpdeskTicketSummary } from "@kuika/kuika-cl-model";

export type HelpdeskTicketGroup = "open" | "pending" | "solved";

export interface IHelpdeskTicketGroups {
  open: IHelpdeskTicketSummary[];
  pending: IHelpdeskTicketSummary[];
  solved: IHelpdeskTicketSummary[];
}

/** The same Open / Pending / Solved split the Zendesk helpdesk shows. */
export function groupOf(status: HelpdeskZendeskStatus): HelpdeskTicketGroup {
  if (status === "pending") return "pending";
  if (status === "solved" || status === "closed") return "solved";
  return "open";
}

const STATUS_GROUP_LABELS: Record<HelpdeskStatusGroup, string> = {
  waiting: "New",
  inProgress: "In progress",
  waitingForInfo: "Waiting for your reply",
  resolved: "Solved",
  rejected: "Closed"
};

const ZENDESK_STATUS_LABELS: Record<HelpdeskZendeskStatus, string> = {
  new: "New",
  open: "In progress",
  pending: "Waiting for your reply",
  solved: "Solved",
  closed: "Closed"
};

/**
 * The status shown to the user. The ITSM's own status name (e.g. "İşlemde") is defined in Turkish
 * and does not follow the language, so the label comes from the fixed codes instead.
 */
export function statusLabel(
  statusGroup: HelpdeskStatusGroup | null | undefined,
  zendeskStatus: HelpdeskZendeskStatus | null | undefined
): string {
  if (statusGroup && STATUS_GROUP_LABELS[statusGroup]) return STATUS_GROUP_LABELS[statusGroup];
  if (zendeskStatus && ZENDESK_STATUS_LABELS[zendeskStatus]) return ZENDESK_STATUS_LABELS[zendeskStatus];
  return "Open";
}

/**
 * Folds `updates` (tickets changed since the last sync) into `current`: a known ticket is replaced,
 * a new one is added. Ordering is left to `groupTickets`.
 */
export function mergeTickets(
  current: readonly IHelpdeskTicketSummary[],
  updates: readonly IHelpdeskTicketSummary[]
): IHelpdeskTicketSummary[] {
  if (updates.length === 0) return [...current];
  const updated: Map<number, IHelpdeskTicketSummary> = new Map<number, IHelpdeskTicketSummary>();
  for (const ticket of updates) updated.set(ticket.ticketNumber, ticket);
  const merged: IHelpdeskTicketSummary[] = current.map((ticket: IHelpdeskTicketSummary): IHelpdeskTicketSummary => {
    const replacement: IHelpdeskTicketSummary | undefined = updated.get(ticket.ticketNumber);
    if (!replacement) return ticket;
    updated.delete(ticket.ticketNumber);
    return replacement;
  });
  return [...updated.values(), ...merged];
}

export function ticketMatchesSearch(ticket: IHelpdeskTicketSummary, searchText: string): boolean {
  const query: string = searchText.trim().toLowerCase();
  if (!query) return true;
  return (
    (ticket.subject ?? "").toLowerCase().includes(query) ||
    (ticket.referenceCode ?? "").toLowerCase().includes(query) ||
    String(ticket.ticketNumber).includes(query)
  );
}

export function groupTickets(tickets: IHelpdeskTicketSummary[], searchText: string): IHelpdeskTicketGroups {
  const groups: IHelpdeskTicketGroups = { open: [], pending: [], solved: [] };
  const sorted: IHelpdeskTicketSummary[] = [...tickets].sort(
    (a: IHelpdeskTicketSummary, b: IHelpdeskTicketSummary): number => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)
  );
  for (const ticket of sorted) {
    if (ticketMatchesSearch(ticket, searchText)) groups[groupOf(ticket.zendeskStatus)].push(ticket);
  }
  return groups;
}

export function formatTicketDate(iso: string): string {
  const date: Date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

/** The day line between a conversation's days, e.g. "12 Oct 2026". */
export function formatTicketDay(iso: string): string {
  const date: Date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/** A message's time within its day, e.g. "20:11". */
export function formatTicketTime(iso: string): string {
  const date: Date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
